/**
 * サンドボックス使用量のスキーマ（利用者向け）
 *
 * ## 全体の実数は一般利用者へ出さない
 *
 * コスト上限（`cost-limit-schemas`）と同じ方針を採る。全体の到達段階だけを載せ、
 * テナント全体の稼働時間・転送量・上限値は含めない。止まった理由が「全体の枠切れ」
 * であることは伝える必要があるが、他の利用者の使用量まで見せる必要はない。
 */
import { z } from 'zod';
/** 到達段階。通常 / 逼迫（80% 以上 100% 未満）/ 超過（100% 以上） */
export const SandboxUsageStageSchema = z.enum(['normal', 'warning', 'exceeded']);
/** 逼迫と判定する使用率（%）。コスト上限と同じ基準を使う */
export const SANDBOX_USAGE_WARNING_PERCENT = 80;
/** 稼働時間と転送量の 2 軸 */
export const SandboxUsageTotalsSchema = z.object({
    runtimeSeconds: z.number(),
    transferBytes: z.number(),
});
/**
 * GET /sandbox/usage レスポンス
 *
 * 自分の使用量と上限は実数で返す（自分で判断・対処できる情報のため）。
 * 全体は段階のみ。
 */
export const SandboxUsageStatusResponseSchema = z.object({
    /** 対象月（YYYY-MM） */
    month: z.string(),
    /** 自分の当月使用量 */
    user: SandboxUsageTotalsSchema,
    /** 利用者 1 人あたりの上限 */
    limits: SandboxUsageTotalsSchema,
    /** 全体の到達段階。本機能を含まない応答でも解析できるよう任意 + 既定値とする */
    systemState: SandboxUsageStageSchema.optional().default('normal'),
});
/**
 * 月あたりの上限。**業務判断値である**（費用としてどこまで許容するかの決めごと）。
 *
 * 4 軸すべてを持つ。上限判定は利用者別・全体の双方を見るため、片方だけ設定できると
 * 「どちらで止まっているのか」を運用側が把握できない状態が作れてしまう。
 */
export const SandboxUsageLimitsSchema = z.object({
    /** 利用者 1 人あたりの稼働時間（秒） */
    perUserRuntimeSeconds: z.number().int().positive(),
    /** 利用者 1 人あたりの転送量（バイト） */
    perUserTransferBytes: z.number().int().positive(),
    /** システム全体の稼働時間（秒） */
    systemRuntimeSeconds: z.number().int().positive(),
    /** システム全体の転送量（バイト） */
    systemTransferBytes: z.number().int().positive(),
});
/** 明示的に保存された上限設定（誰がいつ変えたかを残す） */
export const SandboxUsageLimitsSettingSchema = SandboxUsageLimitsSchema.extend({
    updatedAt: z.string(),
    updatedBy: z.string(),
});
/**
 * GET /admin/sandbox-limits レスポンス
 *
 * 実効値・明示設定・既定値の 3 つを返す。実効値だけでは「既定のままなのか、
 * 既定と同じ値を明示的に設定したのか」が区別できず、既定へ戻す操作も出せない。
 */
export const GetAdminSandboxLimitsResponseSchema = z.object({
    /** 実際に判定へ使われる値 */
    limits: SandboxUsageLimitsSchema,
    /** 明示設定。未設定なら null（＝既定値で動いている） */
    setting: SandboxUsageLimitsSettingSchema.nullable(),
    /** 配布時の既定値。「既定へ戻す」の戻り先を画面が示せるようにする */
    defaults: SandboxUsageLimitsSchema,
});
/** PUT /admin/sandbox-limits リクエスト */
export const UpdateAdminSandboxLimitsRequestSchema = SandboxUsageLimitsSchema;
/** 末尾の 0 を落とす（160.0 → 160、3.20 → 3.2）。桁を増やしても読みにくくしないため */
function trimZeros(value) {
    return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}
/**
 * 稼働時間の表示。
 *
 * **増えていることが見える粒度で出す。** 時間へ丸めると、使い始めの数十分は
 * ずっと「0 時間」のままになり、利用者は自分が計上されているのかどうか分からない。
 */
export function formatSandboxRuntime(seconds) {
    if (seconds < 3600)
        return `${Math.round(seconds / 60)} 分`;
    return `${trimZeros((seconds / 3600).toFixed(1))} 時間`;
}
/**
 * 転送量の表示。
 *
 * GB へ丸めると小さな転送が「0.0 GB」に潰れるため、量に応じて単位を落とす。
 */
export function formatSandboxTransfer(bytes) {
    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;
    if (bytes < MB)
        return `${trimZeros((bytes / KB).toFixed(1))} KB`;
    if (bytes < GB)
        return `${trimZeros((bytes / MB).toFixed(1))} MB`;
    return `${trimZeros((bytes / GB).toFixed(2))} GB`;
}
/**
 * 表示用の使用率（%）。上限が 0 のときは 0 として扱う。
 *
 * **切り捨てる。** 四捨五入だと 99.5% を 100% と掲げ、同じ画面で
 * 「上限に達していません」と説明することになる。判定そのものには使わない
 * （`sandboxUsageRatio` / `deriveSandboxUsageStage`）
 */
export function sandboxUsagePercent(used, limit) {
    return Math.floor(sandboxUsageRatio(used, limit));
}
/**
 * 判定用の使用率（%）。**丸めない。**
 *
 * 確保を止めるかどうかを決めるのはサーバー側の `used >= limit` であり、
 * 丸めた値で判定すると 99.5% を 100 と読んで、まだ確保できる状態の利用者へ
 * 「上限に達しました」と断じることになる。逆に 100.4% を 100 と読むぶんには
 * 差が出ないが、境界の意味をサーバーと揃えるためここでも丸めない
 */
export function sandboxUsageRatio(used, limit) {
    return limit > 0 ? (used / limit) * 100 : 0;
}
/**
 * 2 軸のうち厳しいほうの到達段階を返す。
 *
 * **どちらか一方でも到達すれば確保は止まる**ため、判断は厳しいほうへ寄せる。
 * 判定は丸めない値で行い、サーバー側の上限判定と同じ境界にする
 */
export function deriveSandboxUsageStage(used, limits) {
    const peak = Math.max(sandboxUsageRatio(used.runtimeSeconds, limits.runtimeSeconds), sandboxUsageRatio(used.transferBytes, limits.transferBytes));
    if (peak >= 100)
        return 'exceeded';
    if (peak >= SANDBOX_USAGE_WARNING_PERCENT)
        return 'warning';
    return 'normal';
}
//# sourceMappingURL=sandbox-usage-schemas.js.map